import type { Ref } from "react";
import {
  ShiftCodeField,
  ShiftDescriptionField,
  ShiftNameField,
  SlotField,
  WorkTimeField,
} from "./shift-code-add-dialog-fields";
import {
  LeaveTargetSwitchField,
  OffSwapSwitchField,
} from "./shift-code-add-dialog-extra-fields";
import { WorkTypeField } from "./shift-code-type-tabs";
import type { WorkType } from "./shift-code-add-dialog-utils";

type BasicInfoSectionProps = {
  readonly shiftInputRef?: Ref<HTMLInputElement>;
  readonly shiftId: string;
  readonly color: string;
  readonly shiftIdError?: string;
  readonly shiftName: string;
  readonly shiftNameError?: string;
  readonly shiftDescription: string;
  readonly identityDisabled: boolean;
  readonly shiftCodeDisabled: boolean;
  readonly onShiftIdChange: (value: string) => void;
  readonly onColorChange: (value: string) => void;
  readonly onShiftNameChange: (value: string) => void;
  readonly onShiftDescriptionChange: (value: string) => void;
};

type WorkConditionSectionProps = {
  readonly workType: WorkType;
  readonly workStructureDisabled: boolean;
  readonly isWorkShift: boolean;
  readonly slotIndex: number;
  readonly readonlySlotLabel?: string | null;
  readonly startTime: string;
  readonly endTime: string;
  readonly offSwapEditable: boolean;
  readonly offSwapUnavailableMessage: string;
  readonly offSwapTarget: boolean;
  readonly healthLeaveTarget: boolean;
  readonly sleepOffTarget: boolean;
  readonly onWorkTypeChange: (value: WorkType) => void;
  readonly onSlotIndexChange: (value: number) => void;
  readonly onStartTimeChange: (value: string) => void;
  readonly onEndTimeChange: (value: string) => void;
  readonly onOffSwapTargetChange: (value: boolean) => void;
  readonly onHealthLeaveTargetChange: (value: boolean) => void;
  readonly onSleepOffTargetChange: (value: boolean) => void;
};

export function BasicInfoSection({
  shiftInputRef,
  shiftId,
  color,
  shiftIdError,
  shiftName,
  shiftNameError,
  shiftDescription,
  identityDisabled,
  shiftCodeDisabled,
  onShiftIdChange,
  onColorChange,
  onShiftNameChange,
  onShiftDescriptionChange,
}: BasicInfoSectionProps) {
  return (
    <section className="rounded-lg border border-[#E4E7EC] bg-white p-4 shadow-[0_8px_22px_rgba(16,24,40,0.04)]">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ShiftCodeField
          inputRef={shiftInputRef}
          value={shiftId}
          color={color}
          error={shiftIdError}
          codeDisabled={shiftCodeDisabled}
          onChange={onShiftIdChange}
          onColorChange={onColorChange}
        />
        <ShiftNameField
          value={shiftName}
          error={shiftNameError}
          disabled={identityDisabled}
          onChange={onShiftNameChange}
        />
        <div className="md:col-span-2">
          <ShiftDescriptionField
            value={shiftDescription}
            disabled={identityDisabled}
            onChange={onShiftDescriptionChange}
          />
        </div>
      </div>
    </section>
  );
}

export function WorkConditionSection({
  workType,
  workStructureDisabled,
  isWorkShift,
  slotIndex,
  readonlySlotLabel,
  startTime,
  endTime,
  offSwapEditable,
  offSwapUnavailableMessage,
  offSwapTarget,
  healthLeaveTarget,
  sleepOffTarget,
  onWorkTypeChange,
  onSlotIndexChange,
  onStartTimeChange,
  onEndTimeChange,
  onOffSwapTargetChange,
  onHealthLeaveTargetChange,
  onSleepOffTargetChange,
}: WorkConditionSectionProps) {
  return (
    <section className="overflow-hidden rounded-lg border border-[#E4E7EC] bg-white shadow-[0_8px_22px_rgba(16,24,40,0.04)]">
      <div className="flex flex-col">
        <WorkTypeField
          value={workType}
          disabled={workStructureDisabled}
          onChange={onWorkTypeChange}
        />
        {workStructureDisabled ? (
          <p className="border-b border-[#E4E7EC] bg-[#F8FAFC] px-4 py-2 text-xs font-medium text-[#667085]">
            기본 근무코드는 타입과 구분을 변경할 수 없습니다.
          </p>
        ) : null}
        {isWorkShift ? (
          <div className="flex flex-col gap-4 p-4">
            <SlotField
              value={slotIndex}
              disabled={workStructureDisabled}
              readonlySlotLabel={readonlySlotLabel}
              onChange={onSlotIndexChange}
            />
            <WorkTimeField
              enabled
              startTime={startTime}
              endTime={endTime}
              onStartTimeChange={onStartTimeChange}
              onEndTimeChange={onEndTimeChange}
            />
          </div>
        ) : (
          // 근무가 아닌 코드에만 노출한다. 보건휴가·수면OFF 는 OFF 대체와 서버 규칙이
          // 같지만(type!=='근무' · 그룹당 1개) OFF 자체를 빼는 추가 조건이 없어,
          // offSwapEditable 이 false 여도 지정할 수 있다.
          <div className="flex flex-col gap-4 p-4">
            {offSwapEditable ? (
              <OffSwapSwitchField
                checked={offSwapTarget}
                disabled={workStructureDisabled}
                onChange={onOffSwapTargetChange}
              />
            ) : (
              <div className="rounded-lg border border-[#E4E7EC] bg-[#F8FAFC] px-4 py-3">
                <p className="text-sm font-semibold text-[#344054]">
                  OFF 대체 조건
                </p>
                <p className="mt-1 break-keep text-xs leading-5 text-[#667085]">
                  {offSwapUnavailableMessage}
                </p>
              </div>
            )}
            {/* workStructureDisabled 로 잠그지 않는다 — 그것은 '타입·구분 변경 불가'
                를 뜻하고, 휴가 대상 지정과는 무관하다. 서버도 type!=='근무' 만 본다.
                기본 휴가코드(O·주)도 대상으로 지정할 수 있어야 한다. */}
            <LeaveTargetSwitchField
              id="edit-shift-health-leave"
              label="보건휴가 부여 대상"
              description="보건휴가 자동 부여가 켜지면 이 코드로 월 1개를 배정합니다. 그룹당 1개만 지정할 수 있습니다."
              checked={healthLeaveTarget}
              disabled={false}
              onChange={onHealthLeaveTargetChange}
            />
            <LeaveTargetSwitchField
              id="edit-shift-sleep-off"
              label="수면OFF 부여 대상"
              description="나이트 연번이 설정한 주기에 도달하면 이 코드를 1개 부여합니다. 그룹당 1개만 지정할 수 있습니다."
              checked={sleepOffTarget}
              disabled={false}
              onChange={onSleepOffTargetChange}
            />
          </div>
        )}
      </div>
    </section>
  );
}
